/**
 * MyPendingTasks widget — tasks assigned to the current operator.
 *
 * Migrated to live API in Wave 3.6. Reads GET /v1/processes/tasks
 * filtered to status=pending. Server-side filter by assigned_to isn't
 * exposed yet so we filter client-side against the current worker id
 * pulled from the axios interceptor's token context.
 *
 * Priority buckets (backend uses strings: 'high' | 'medium' | 'low') map
 * to the same red / amber / grey flags the mock used. \`overdue\` is
 * derived client-side from due_date vs now so the chip updates between
 * renders without backend churn.
 *
 * The widget intentionally caps the rendering at the current page's
 * viewport — scrolling lives in the parent widget frame. If the user
 * has more than ~20 pending tasks, they should use the Tasks page
 * (not the dashboard glance) so no count paging is surfaced here.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Chip, CircularProgress, LinearProgress, List, ListItem, Paper,
  Typography,
} from '@mui/material';
import { AccessTimeOutlined, FlagOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ApiError, processesApi } from '../../../../api';
import { WidgetErrorState } from '../../../../components/primitives';
import type { ProcessTask } from '../../../../api/processes';
import { useTranslation } from 'react-i18next';
import { translatePriority, type WidgetTranslator } from './widgetDisplay';
import WidgetSummaryLine from './WidgetSummaryLine';
import { translateApiError } from '../../../../i18n/translateApiError';
import WidgetEmptyState from '../../../../components/primitives/WidgetEmptyState';
import { TaskAltOutlined } from '@mui/icons-material';

import { getSystemTaskTitle } from '../../../Processes/processDisplay';

type Priority = 'high' | 'medium' | 'low';

function priorityColor(p: string | null | undefined): 'error.main' | 'warning.main' | 'text.disabled' {
  if (p === 'high' || p === 'urgent') return 'error.main';
  if (p === 'medium') return 'warning.main';
  return 'text.disabled';
}

function normalizePriority(p: string | null | undefined): Priority {
  if (p === 'high' || p === 'urgent') return 'high';
  if (p === 'medium') return 'medium';
  return 'low';
}

function formatDue(dueIso: string | null | undefined, overdue: boolean, t: WidgetTranslator): string {
  if (!dueIso) return '—';
  if (overdue) return t('dashboard:widgets.myPendingTasks.overdue');
  const diffMs = new Date(dueIso).getTime() - Date.now();
  if (diffMs <= 0) return t('dashboard:widgets.myPendingTasks.now');
  const hours = diffMs / 3_600_000;
  if (hours < 24) return t('dashboard:widgets.myPendingTasks.dueInHours', { count: Math.max(1, Math.round(hours)) });
  const days = hours / 24;
  if (days < 14) return t('dashboard:widgets.myPendingTasks.dueInDays', { count: Math.round(days) });
  return t('dashboard:widgets.myPendingTasks.dueInWeeks', { count: Math.round(days / 7) });
}

const MyPendingTasks: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<ProcessTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await processesApi.processTasksApi.list({ status: 'pending' });
        if (cancelled) return;
        // Sort: overdue → high → medium → low, then by due_date ascending.
        const priOrder = { high: 0, medium: 1, low: 2 } as const;
        const sorted = [...rows].sort((a, b) => {
          const ao = a.due_date ? new Date(a.due_date).getTime() : Number.MAX_SAFE_INTEGER;
          const bo = b.due_date ? new Date(b.due_date).getTime() : Number.MAX_SAFE_INTEGER;
          const pa = priOrder[normalizePriority(a.priority)];
          const pb = priOrder[normalizePriority(b.priority)];
          if (pa !== pb) return pa - pb;
          return ao - bo;
        });
        setTasks(sorted);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof ApiError ? translateApiError(e, t) : t('dashboard:widgets.myPendingTasks.loadError'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [t]);

  const now = Date.now();
  const withOverdue = useMemo(
    () => tasks.map((t) => ({
      task: t,
      overdue: !!(t.due_date && new Date(t.due_date).getTime() < now),
    })),
    [tasks, now],
  );
  const overdueCount = withOverdue.filter((t) => t.overdue).length;

  if (loading) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return <WidgetErrorState error={error} />;
  }

  if (withOverdue.length === 0) {
    return (
      <WidgetEmptyState icon={TaskAltOutlined} title={t('dashboard:widgets.myPendingTasks.noTasks')} />
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <WidgetSummaryLine
        action={overdueCount > 0 ? (
          <Chip label={t('dashboard:widgets.myPendingTasks.overdueCount', { count: overdueCount })} size="small" color="error" sx={{ height: 20, fontSize: '0.75rem' }} />
        ) : undefined}
      >
        {t('dashboard:widgets.myPendingTasks.pendingCount', { count: withOverdue.length })}
      </WidgetSummaryLine>
      <List sx={{ py: 0, flex: 1, overflow: 'auto' }}>
        {withOverdue.map(({ task, overdue }, i) => {
          const processName = task.instance_id
            ? t('dashboard:widgets.myPendingTasks.processRef', { id: task.instance_id.slice(0, 8) })
            : '—';
          return (
            <ListItem
              key={task.id}
              component={Paper}
              elevation={0}
              // WHERE THIS CLICK IS ALLOWED TO GO (2026-07-31)
              //
              // Two wrong answers shipped before this one:
              //   `/processes/tasks/my` — never a route, so it fell to the
              //     catch-all and bounced back to /dashboard.
              //   `/me/tasks` — a real route, and still unreachable from here.
              //     `resolveRoleLanding` sends every admin-tier role
              //     (super_admin/admin/hr_manager/owner/org_admin) out of any
              //     `/me/*` path, and PortalBoundary applies it with
              //     `<Navigate replace />` — so the URL never even records the
              //     attempt. That is exactly what the owner saw: click, and the
              //     address bar still says /dashboard. "Admin roles never enter
              //     the worker shell" is deliberate (ADR-031), not a bug to
              //     route around.
              //
              // So this widget, which renders in the ADMIN shell, has to land on
              // an admin route. The task's own process instance is both reachable
              // and the more useful destination: an admin looking at a pending
              // task wants the process it belongs to, not a personal to-do list.
              onClick={() => navigate(
                task.instance_id
                  ? `/processes/instances/${task.instance_id}`
                  : '/processes/instances/active',
              )}
              sx={{ py: 1, mb: i < withOverdue.length - 1 ? 0.5 : 0,
                borderRadius: 1, bgcolor: 'background.paper',
                display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
                cursor: 'pointer',
                border: '1px solid transparent',
                transition: 'border-color 120ms',
                '&:hover': { borderColor: 'divider' },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', mb: 0.5 }}>
                <FlagOutlined titleAccess={translatePriority(t, task.priority)} sx={{ fontSize: '0.9rem', color: priorityColor(task.priority), mr: 0.75, flexShrink: 0 }} />
                <Typography variant="body2"
                  sx={{ fontWeight: 600, color: 'primary.main', flex: 1, cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' },
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {getSystemTaskTitle(t, task, task.node_id, task.title)}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1, flexShrink: 0 }}>
                  <AccessTimeOutlined sx={{ fontSize: '0.75rem', color: overdue ? 'error.main' : 'text.secondary' }} />
                  <Typography variant="caption"
                    sx={{ color: overdue ? 'error.main' : 'text.secondary',
                      fontWeight: overdue ? 700 : 400, fontSize: '0.75rem' }}>
                    {formatDue(task.due_date, overdue, t)}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', flexShrink: 0 }}>
                  {processName}
                </Typography>
                {task.progress > 0 && (
                  <LinearProgress variant="determinate" value={task.progress}
                    sx={{ flex: 1, height: 3, borderRadius: 8, bgcolor: 'grey.100',
                      '& .MuiLinearProgress-bar': { bgcolor: overdue ? 'error.main' : 'primary.main' } }} />
                )}
              </Box>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
};

export default MyPendingTasks;
